import { Button, Checkbox, Chip, CircularProgress, Paper } from '@mui/material';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AppPaperContent from 'src/app/common/components/AppPaperContent';
import AppTable from 'src/app/common/components/AppTable/AppTable';
import PruFilter from 'src/app/common/components/PruTable/PruFilter';
import {
  BdmSentBackFromCode,
  PolicyTaskStatus,
  policyTaskStatuses,
  policyTaskStatusMap,
} from 'src/app/modules/OpmPd/constants/common.constant';
import { endBdmHoldPolicyCases } from 'src/app/modules/OpmPd/network/opm-pd-bdm';
import { formatDateTime } from 'src/app/modules/OpmPd/utils/commonHelper';
import { alertDialog } from 'src/utils/alertDialog';
import { useHoldPolicyListApi } from './useHoldPolicyListApi';
import ExportButton from 'src/app/modules/OpmPd/components/ExportButton/ExportButton';
import { PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import _isEqual from 'lodash/isEqual';

const initialFilters = {};

const HoldPolicyList: FC = () => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();
  const [isSubmittingEndCases, setIsSubmittingEndCases] = useState(false);
  const [selectedPolicyIds, setSelectedPolicyIds] = useState<string[]>([]);

  const { filterState, holdPolicies, isLoading, setFilterState, refreshData, onSort } = useHoldPolicyListApi({
    initialParams: initialFilters,
  });

  const history = useHistory();

  const selectableHoldPolicyIds = useMemo(() => {
    return (
      holdPolicies?.items
        ?.filter((policy) => policy.policyTaskStatus === PolicyTaskStatus.HOLD)
        .map((policy) => String(policy.id)) || []
    );
  }, [holdPolicies]);

  const selectedCountOnPage = selectableHoldPolicyIds.filter((id) => selectedPolicyIds.includes(id)).length;
  const isAllSelectedOnPage =
    selectableHoldPolicyIds.length > 0 && selectedCountOnPage === selectableHoldPolicyIds.length;
  const isIndeterminateOnPage = selectedCountOnPage > 0 && selectedCountOnPage < selectableHoldPolicyIds.length;

  const handleSelectAllOnPage = (checked: boolean) => {
    setSelectedPolicyIds((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, ...selectableHoldPolicyIds]));
      }

      const holdPolicyIdSet = new Set(selectableHoldPolicyIds);
      return prev.filter((id) => !holdPolicyIdSet.has(id));
    });
  };

  const handleTogglePolicy = (policyId: string) => {
    if (!selectableHoldPolicyIds.includes(policyId)) {
      return;
    }

    setSelectedPolicyIds((prev) => {
      if (prev.includes(policyId)) {
        return prev.filter((id) => id !== policyId);
      }

      return [...prev, policyId];
    });
  };

  const endCase = async () => {
    if (!selectedPolicyIds.length || isSubmittingEndCases) return;

    const { isConfirmed } = await alertDialog.fire({
      title: Translation('opm_pd.endCase'),
      description: Translation('opm_pd.endCaseConfirmationText'),
    });
    if (!isConfirmed) return;
    setIsSubmittingEndCases(true);
    try {
      await endBdmHoldPolicyCases({ policyIds: selectedPolicyIds }, dispatch);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: Translation('opm_pd.success'),
            content: Translation('opm_pd.endCaseSuccessfully'),
          },
        ]),
      );
      setSelectedPolicyIds([]);
      refreshData();
    } catch (error) {
    } finally {
      setIsSubmittingEndCases(false);
    }
  };

  const goToReassignAgentPage = () => {
    if (selectedPolicyIds.length > 500) {
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: Translation('opm_pd.error'),
            content: Translation('opm_pd.onlyUpTo500PoliciesCanBeProcessedAtOnceText'),
          },
        ]),
      );
      return;
    }
    history.push(
      `/opm-pd/policy-transfer-request/batch-files/reassign-agent-for-hold-policies?policyIds=${selectedPolicyIds.join(
        ',',
      )}`,
    );
  };

  const filterPolicyTaskStatuses = useMemo(() => {
    return policyTaskStatuses.filter((status) =>
      [PolicyTaskStatus.HOLD, PolicyTaskStatus.END_BDM, PolicyTaskStatus.END_SYSTEM].includes(status.code),
    );
  }, []);

  const holdPolicyIds = useMemo(() => {
    return holdPolicies?.items?.map((policy) => String(policy.id)) || [];
  }, [holdPolicies?.items]);
  const holdPolicyIdsRef = useRef(holdPolicyIds);

  useEffect(() => {
    if (_isEqual(holdPolicyIdsRef.current, holdPolicyIds)) {
      return;
    }
    setSelectedPolicyIds([]);
    holdPolicyIdsRef.current = holdPolicyIds;
  }, [holdPolicyIds]);

  return (
    <>
      <PruFilter
        title={Translation('opm_pd.filter')}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'policyNumber',
            initialValue: '',
            displayName: Translation('opm_pd.policyNumber'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'currentAgentCode',
            initialValue: '',
            displayName: Translation('opm_pd.currentAgentCode'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'soldUnit',
            initialValue: '',
            displayName: Translation('opm_pd.soldUnit'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'importedAgent',
            initialValue: '',
            displayName: Translation('opm_pd.importedAgent'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'suggestedAgent',
            initialValue: '',
            displayName: Translation('opm_pd.suggestedAgent'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'otherQualifiedAgent',
            initialValue: '',
            displayName: Translation('opm_pd.otherQualifiedAgent'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'batchNo',
            initialValue: '',
            displayName: Translation('opm_pd.fromBatch'),
          },
          {
            type: PruFilterItemType.DATE,
            field: 'holdStartDate',
            displayName: Translation('opm_pd.holdStartDate'),
          },
          {
            type: PruFilterItemType.DATE,
            field: 'holdExpireDate',
            displayName: Translation('opm_pd.holdExpireDate'),
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'sentBackFrom',
            initialValue: '',
            displayName: Translation('opm_pd.sendBackFrom'),
            choices: [
              {
                displayName: Translation('opm_pd.all'),
                value: '',
              },
              ...[BdmSentBackFromCode.LM, BdmSentBackFromCode.NO_QUALIFIED_AGENT].map((item) => ({
                displayName: item,
                value: item,
              })),
            ],
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'reasonForSendingBack',
            initialValue: '',
            displayName: Translation('opm_pd.reasonForSendingBack'),
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'policyTaskStatus',
            initialValue: '',
            displayName: Translation('opm_pd.status'),
            choices: [
              {
                displayName: Translation('opm_pd.all'),
                value: '',
              },
              ...filterPolicyTaskStatuses.map((policyTaskStatus) => ({
                displayName: policyTaskStatus.code,
                value: policyTaskStatus.code,
              })),
            ],
          },
        ]}
        onChangeFilter={(data) => {
          setFilterState({ ...filterState, ...data, page: 1 });
        }}
        fetchData={refreshData}
      />
      <AppTable
        title={`${Translation('opm_pd.holdPolicyList')} ${
          selectedPolicyIds.length ? `(${selectedPolicyIds.length} policy selected)` : ''
        }`}
        disableBulkSelect
        startHeaderActions={
          <>
            <ExportButton module="bdmHoldPolicies" />
          </>
        }
        stickyHeader
        rowsPerPageOptions={[10, 25, 50, 100, 500]}
        operationDef={[]}
        columnDef={[
          // Add columns here
          {
            isId: true,
            hidden: true,
            keyIndex: 'id',
            displayName: '',
            renderData: () => '',
            sortable: true,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'policyId',
            displayName: (
              <>
                <Checkbox
                  checked={isAllSelectedOnPage}
                  indeterminate={isIndeterminateOnPage}
                  onChange={(event) => handleSelectAllOnPage(event.target.checked)}
                  sx={{
                    color: 'white',
                    '&.Mui-checked': { color: 'white' },
                    '&.MuiCheckbox-indeterminate': { color: 'white' },
                  }}
                />
              </>
            ),
            renderData: (row) => {
              const policyId = String(row.id ?? '');
              const isSelectable = selectableHoldPolicyIds.includes(policyId);
              if (!isSelectable) return <></>;
              return (
                <>
                  <Checkbox
                    checked={selectedPolicyIds.includes(policyId)}
                    onClick={() => handleTogglePolicy(policyId)}
                  />
                </>
              );
            },
            padding: 'checkbox',
            sortable: false,
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'policyNumber',
            displayName: Translation('opm_pd.policyNumber'),
            renderData: (row) => {
              return row.policyNumber;
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'policyStatusCode',
            displayName: Translation('opm_pd.policyStatusCode'),
            renderData: (row) => {
              return row.policyStatusCode;
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'currentAgentCode',
            displayName: Translation('opm_pd.currentAgentCode'),
            renderData: (row) => {
              return row.currentAgentCode;
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'soldUnit',
            displayName: Translation('opm_pd.soldUnit'),
            renderData: (row) => {
              return row.soldUnit;
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'importedAgent',
            displayName: Translation('opm_pd.importedAgent'),
            renderData: (row) => {
              return row.importedAgent;
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'suggestedAgent',
            displayName: Translation('opm_pd.suggestedAgent'),
            renderData: (row) => {
              return row.suggestedAgent;
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'otherQualifiedAgent',
            displayName: Translation('opm_pd.otherQualifiedAgent'),
            renderData: (row) => {
              const selectedQualifiedAgent = row.isSelectedOtherAgent ? row.selectedAgent : '';
              return selectedQualifiedAgent;
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'selectedAgent',
            displayName: Translation('opm_pd.selectedAgent'),
            renderData: (row) => {
              return row.selectedAgent;
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'selectedAgentName',
            displayName: Translation('opm_pd.selectedAgentName'),
            renderData: (row) => {
              return row.selectedAgentName;
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'holdStartDate',
            displayName: Translation('opm_pd.holdStartDate'),
            renderData: (row) => {
              return row.holdStartDate ? formatDateTime(row.holdStartDate, 'DD/MM/YYYY') : '';
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'actionedExpireAt',
            displayName: Translation('opm_pd.holdExpireDate'),
            renderData: (row) => {
              return row.actionedExpireAt ? formatDateTime(row.actionedExpireAt, 'DD/MM/YYYY') : '';
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'sentBackFrom',
            displayName: Translation('opm_pd.sentBackFrom'),
            renderData: (row) => {
              return row.sentBackFrom;
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'actionedReason',
            displayName: Translation('opm_pd.reasonForSendingBack'),
            renderData: (row) => {
              return row.actionedReason;
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'fromBatch',
            displayName: Translation('opm_pd.fromBatch'),
            renderData: (row) => {
              return row.batchNo;
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'policyTaskStatus',
            displayName: Translation('opm_pd.status'),
            renderData: (row) => {
              const status = policyTaskStatusMap[row.policyTaskStatus];
              return status ? <Chip label={row.policyTaskStatus} color={status.color as any} size="small" /> : <></>;
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
        ]}
        isLoading={isLoading}
        onRefresh={refreshData}
        dataSource={holdPolicies?.items || []}
        totalPages={holdPolicies?.totalPages}
        totalRecords={holdPolicies?.total}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({ ...filterState, page, limit: rowsPerPage });
          refreshData();
        }}
      />
      <Paper>
        <AppPaperContent sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            startIcon={isSubmittingEndCases ? <CircularProgress size={20} /> : null}
            variant="outlined"
            color="error"
            onClick={endCase}
            disabled={isSubmittingEndCases || selectedPolicyIds.length === 0}
          >
            {Translation('opm_pd.endCase')}
          </Button>
          <Button
            startIcon={isSubmittingEndCases ? <CircularProgress size={20} /> : null}
            variant="contained"
            color="secondary"
            onClick={goToReassignAgentPage}
            disabled={isSubmittingEndCases || selectedPolicyIds.length === 0}
          >
            {Translation('opm_pd.reassignAgent')}
          </Button>
        </AppPaperContent>
      </Paper>
    </>
  );
};

export default HoldPolicyList;
